/**
 * @module AdapterConfigForm
 * @package @geenius/adapters/solidjs-css
 * @description Renders the vanilla CSS adapter configuration form for the
 * SolidJS CSS variant. The component keeps provider selection and credential
 * inputs local while delegating persistence to the caller.
 */

import type { AdapterDomain, DomainAdapterConfig } from "@geenius/adapters";
import { DOMAIN_LABELS, getProvidersForDomain } from "@geenius/adapters";
import type { JSX } from "solid-js";
import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { cx } from "../lib/cx";

interface ProviderOptionField {
  key: string;
  label: string;
  description: string;
  placeholder?: string;
  inputMode?: "text" | "numeric";
}

const PROVIDER_OPTION_FIELDS: Partial<
  Record<AdapterDomain, Record<string, ProviderOptionField[]>>
> = {
  db: {
    convex: [
      {
        key: "deployment",
        label: "Deployment",
        description: "Convex deployment or project slug.",
        placeholder: "team:project",
      },
    ],
    neon: [
      {
        key: "database",
        label: "Database",
        description: "Database name or branch label for downstream wiring.",
        placeholder: "main",
      },
    ],
    cloudflareKV: [
      {
        key: "namespace",
        label: "KV Namespace",
        description: "Worker binding name used by the downstream KV adapter.",
        placeholder: "ADAPTERS_KV",
      },
      {
        key: "defaultTtlSeconds",
        label: "Default TTL Seconds",
        description: "Optional TTL for cache/session style records.",
        placeholder: "300",
        inputMode: "numeric",
      },
    ],
    memory: [
      {
        key: "seed",
        label: "Fixture Seed",
        description: "Optional deterministic seed for local test data.",
        placeholder: "demo",
      },
    ],
  },
};

const LAUNCH_DB_PROVIDER_IDS = new Set([
  "convex",
  "neon",
  "cloudflareKV",
  "memory",
]);

function getSelectableProviders(domain: AdapterDomain) {
  const providers = getProvidersForDomain(domain);
  if (domain !== "db") return providers;
  return providers.filter(
    (entry) =>
      entry.id === "localStorage" || LAUNCH_DB_PROVIDER_IDS.has(entry.id),
  );
}

function getOptionFields(
  domain: AdapterDomain,
  provider: string,
): ProviderOptionField[] {
  return PROVIDER_OPTION_FIELDS[domain]?.[provider] ?? [];
}

function stringifyOptionValue(value: unknown): string {
  if (value === undefined || value === null) {
    return "";
  }
  return String(value);
}

function coerceOptionValue(field: ProviderOptionField, value: string): unknown {
  const trimmed = value.trim();
  if (trimmed === "") {
    return "";
  }
  if (field.inputMode !== "numeric") {
    return trimmed;
  }
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : trimmed;
}

function validateOptionValues(
  domain: AdapterDomain,
  provider: string,
  optionValues: Record<string, string>,
): string | null {
  if (domain === "db" && provider === "cloudflareKV") {
    if (!optionValues.namespace?.trim()) {
      return "KV namespace is required";
    }
    const ttlValue = optionValues.defaultTtlSeconds?.trim();
    if (ttlValue) {
      const ttlSeconds = Number(ttlValue);
      if (!Number.isInteger(ttlSeconds) || ttlSeconds <= 0) {
        return "Default TTL seconds must be a positive integer";
      }
    }
  }

  for (const field of getOptionFields(domain, provider)) {
    if (field.inputMode !== "numeric") continue;
    const raw = optionValues[field.key]?.trim();
    if (!raw) continue;
    if (!Number.isFinite(Number(raw))) {
      return `${field.label} must be a number`;
    }
  }

  return null;
}

/**
 * Props accepted by the SolidJS CSS adapter configuration form.
 *
 * @property domain Adapter domain being configured in the current dialog
 * @property initialConfig Existing configuration values used to hydrate the form
 * @property onSave Async or sync handler that persists the submitted configuration
 * @property onCancel Optional callback invoked when the user dismisses the form
 * @property class Optional class merged onto the form root
 * @property titleId Optional identifier applied to the form title for dialog labelling
 */
export interface AdapterConfigFormProps {
  domain: AdapterDomain;
  initialConfig?: DomainAdapterConfig;
  onSave: (config: DomainAdapterConfig) => Promise<void> | void;
  onCancel?: () => void;
  class?: string;
  titleId?: string;
}

/**
 * Renders the adapter configuration workflow for a single adapter domain.
 *
 * @param props Component props controlling the selected domain and form callbacks
 * @returns A provider chooser and credential form styled with the CSS variant
 * @example
 * ```tsx
 * <AdapterConfigForm
 *   domain="auth"
 *   onSave={async (config) => saveConfig('auth', config)}
 *   onCancel={() => setOpen(false)}
 * />
 * ```
 */
export function AdapterConfigForm(props: AdapterConfigFormProps): JSX.Element {
  // Derive the selectable provider list reactively so prop-driven domain
  // changes flip the chooser, hydrated value, and provider-specific option
  // fields together instead of pinning to the initial domain.
  const providers = createMemo(() => getSelectableProviders(props.domain));
  const initialProviderId = (): string =>
    providers().find((entry) => entry.id === props.initialConfig?.provider)
      ?.id ??
    providers()[0]?.id ??
    "";

  const [provider, setProvider] = createSignal(initialProviderId());
  const [apiKey, setApiKey] = createSignal(props.initialConfig?.apiKey ?? "");
  const [baseUrl, setBaseUrl] = createSignal(
    props.initialConfig?.baseUrl ?? "",
  );
  const [optionValues, setOptionValues] = createSignal<Record<string, string>>(
    Object.fromEntries(
      Object.entries(props.initialConfig?.options ?? {}).map(([key, value]) => [
        key,
        stringifyOptionValue(value),
      ]),
    ),
  );
  const [isSaving, setIsSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const selectedMeta = () =>
    providers().find((entry) => entry.id === provider());

  const needsApiKey = () => selectedMeta()?.requiresApiKey ?? false;
  const needsBaseUrl = () => selectedMeta()?.requiresBaseUrl ?? false;
  const optionFields = (): ProviderOptionField[] =>
    getOptionFields(props.domain, provider());

  // Reset provider-specific state whenever the domain or initialConfig
  // changes so stale credentials/options can't leak from a prior domain.
  createEffect(() => {
    const domainKey = props.domain;
    const config = props.initialConfig;
    void domainKey;
    void config;
    const list = providers();
    const nextProvider =
      list.find((entry) => entry.id === config?.provider)?.id ??
      list[0]?.id ??
      "";
    setProvider(nextProvider);
    setApiKey(config?.apiKey ?? "");
    setBaseUrl(config?.baseUrl ?? "");
    setOptionValues(
      Object.fromEntries(
        Object.entries(config?.options ?? {}).map(([key, value]) => [
          key,
          stringifyOptionValue(value),
        ]),
      ),
    );
    setError(null);
  });

  createEffect(() => {
    if (!needsApiKey() && apiKey()) {
      setApiKey("");
    }
    if (!needsBaseUrl() && baseUrl()) {
      setBaseUrl("");
    }
  });

  const handleProviderChange = (nextProvider: string): void => {
    if (nextProvider === provider()) return;
    setProvider(nextProvider);
    setApiKey("");
    setBaseUrl("");
    setOptionValues({});
  };

  const handleSubmit = async (event: Event): Promise<void> => {
    event.preventDefault();
    setError(null);
    if (!provider()) {
      setError("Select a provider");
      return;
    }
    if (needsApiKey() && !apiKey().trim()) {
      setError("API key is required");
      return;
    }
    if (needsBaseUrl() && !baseUrl().trim()) {
      setError("Base URL is required");
      return;
    }
    const optionError = validateOptionValues(
      props.domain,
      provider(),
      optionValues(),
    );
    if (optionError) {
      setError(optionError);
      return;
    }
    setIsSaving(true);
    try {
      const config: DomainAdapterConfig = { provider: provider() };
      if (needsApiKey() && apiKey().trim()) {
        config.apiKey = apiKey().trim();
      }
      if (needsBaseUrl() && baseUrl().trim()) {
        config.baseUrl = baseUrl().trim();
      }
      const fields = optionFields();
      const current = optionValues();
      const options = Object.fromEntries(
        fields
          .map((field) => [
            field.key,
            coerceOptionValue(field, current[field.key] ?? ""),
          ])
          .filter(([, value]) => value !== ""),
      );
      if (Object.keys(options).length > 0) {
        config.options = options;
      }
      await props.onSave(config);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      class={cx("gn-adapters-form", props.class)}
      noValidate
    >
      <h3 id={props.titleId} class="gn-adapters-form__title">
        Configure {DOMAIN_LABELS[props.domain]}
      </h3>
      <p class="gn-adapters-form__subtitle">
        Select and configure your preferred provider.
      </p>

      <Show when={error()}>
        <div
          class="gn-adapters-alert gn-adapters-alert--error gn-adapters-alert--spaced"
          role="alert"
        >
          {error()}
        </div>
      </Show>

      <fieldset class="gn-adapters-field gn-adapters-field--provider">
        <legend class="gn-adapters-field__label">Provider</legend>
        <div class="gn-adapters-provider-grid">
          <For each={providers()}>
            {(p) => (
              <label
                class={cx(
                  "gn-adapters-provider-option",
                  provider() === p.id &&
                    "gn-adapters-provider-option--selected",
                )}
              >
                <input
                  type="radio"
                  name={`gn-adapters-${props.domain}-provider`}
                  value={p.id}
                  checked={provider() === p.id}
                  onChange={() => handleProviderChange(p.id)}
                  class="gn-adapters-provider-option__input"
                />
                <span class="gn-adapters-provider-option__name">{p.name}</span>
                <span class="gn-adapters-provider-option__desc">
                  {p.description}
                </span>
                <span
                  class={cx(
                    "gn-adapters-tier-badge",
                    `gn-adapters-tier-badge--${p.tier}`,
                  )}
                >
                  {p.tier}
                </span>
              </label>
            )}
          </For>
        </div>
      </fieldset>

      <Show when={needsApiKey()}>
        <div class="gn-adapters-field">
          <label
            class="gn-adapters-field__label"
            for={`gn-adapters-${props.domain}-apiKey`}
          >
            API Key
          </label>
          <input
            id={`gn-adapters-${props.domain}-apiKey`}
            type="password"
            value={apiKey()}
            onInput={(e) => setApiKey(e.currentTarget.value)}
            placeholder={`Enter ${selectedMeta()?.name ?? provider()} API key`}
            class="gn-adapters-field__input"
            autocomplete="off"
            required
            aria-invalid={
              error() === "API key is required" ? "true" : undefined
            }
          />
        </div>
      </Show>

      <Show when={needsBaseUrl()}>
        <div class="gn-adapters-field">
          <label
            class="gn-adapters-field__label"
            for={`gn-adapters-${props.domain}-url`}
          >
            Base URL
          </label>
          <input
            id={`gn-adapters-${props.domain}-url`}
            type="url"
            value={baseUrl()}
            onInput={(e) => setBaseUrl(e.currentTarget.value)}
            placeholder="https://..."
            class="gn-adapters-field__input"
            required
            aria-invalid={
              error() === "Base URL is required" ? "true" : undefined
            }
          />
        </div>
      </Show>

      <For each={optionFields()}>
        {(field) => (
          <div class="gn-adapters-field">
            <label
              class="gn-adapters-field__label"
              for={`gn-adapters-${props.domain}-${field.key}`}
            >
              {field.label}
            </label>
            <input
              id={`gn-adapters-${props.domain}-${field.key}`}
              type="text"
              inputMode={field.inputMode}
              value={optionValues()[field.key] ?? ""}
              onInput={(e) => {
                const next = e.currentTarget.value;
                setOptionValues((current) => ({
                  ...current,
                  [field.key]: next,
                }));
              }}
              placeholder={field.placeholder}
              class="gn-adapters-field__input"
              aria-describedby={`gn-adapters-${props.domain}-${field.key}-hint`}
            />
            <p
              id={`gn-adapters-${props.domain}-${field.key}-hint`}
              class="gn-adapters-field__hint"
            >
              {field.description}
            </p>
          </div>
        )}
      </For>

      <div class="gn-adapters-actions">
        <Show when={props.onCancel}>
          <button
            type="button"
            onClick={() => props.onCancel?.()}
            class="gn-adapters-btn gn-adapters-btn--ghost"
          >
            Cancel
          </button>
        </Show>
        <button
          type="submit"
          disabled={isSaving() || !provider()}
          class="gn-adapters-btn gn-adapters-btn--primary"
        >
          {isSaving() ? "Saving..." : "Save Configuration"}
        </button>
      </div>
    </form>
  );
}
